import { useState, useEffect } from 'react'
import { useAppDispatch, useAppSelector } from '@/store/hooks'
import { fetchApiKeysSaga, createApiKeySaga } from '@/store/apiKeys/apiKeysSlice'
import type { ApiKey } from '@/store/apiKeys/apiKeysSlice'
import { selectApiKeys, selectApiKeysLoading, selectNewApiKey } from '@/store/apiKeys/apiKeysSelectors'
import { selectIsLoading } from '@/store/ui/uiSelectors'
import { useWorkspace } from '@/contexts/WorkspaceContext'
import WorkspaceSettingsTitle from '../WorkspaceSettingsTitle/WorkspaceSettingsTitle'
import FormColumn from '@/components/ui/Form/components/FormColumn/FormColumn'
import FormDivider from '@/components/ui/Form/components/FormDivider/FormDivider'
import FormTitle from '@/components/ui/Form/components/FormTitle/FormTitle'
import FormLabel from '@/components/ui/Form/components/FormLabel/FormLabel'
import FormError from '@/components/ui/Form/components/FormError/FormError'
import FormRow from '@/components/ui/Form/components/FormRow/FormRow'
import Input from '@/components/ui/Input/Input'
import Button from '@/components/ui/Button/Button'
import { TextNorm } from '@/components/ui/Text'
import ApiKeyRow from './components/ApiKeyRow/ApiKeyRow'
import NewKeyBanner from './components/NewKeyBanner/NewKeyBanner'
import PlateLoader from '@/components/ui/Loader/PlateLoader'
import css from './WorkspaceSettingsModalApi.module.css'

const WorkspaceSettingsModalApi = () => {
    const dispatch = useAppDispatch()
    const { workspace } = useWorkspace()
    const keys = useAppSelector(selectApiKeys)
    const isLoading = useAppSelector(selectApiKeysLoading)
    const newKey = useAppSelector(selectNewApiKey)
    const isCreating = useAppSelector(selectIsLoading('create-api-key'))

    const [name, setName] = useState('')
    const [nameError, setNameError] = useState<string | null>(null)
    const [showForm, setShowForm] = useState(false)

    useEffect(() => {
        if (workspace?.id) {
            dispatch(fetchApiKeysSaga(workspace.id))
        }
    }, [workspace?.id, dispatch])

    useEffect(() => {
        if (newKey) {
            setShowForm(false)
            setName('')
            setNameError(null)
        }
    }, [newKey])

const handleCreate = () => {
        const trimmed = name.trim()
        if (!trimmed) {
            setNameError('Please enter a name for this key')
            return
        }
        if (!workspace?.id) return
        dispatch(createApiKeySaga({ workspaceId: workspace.id, name: trimmed }))
        setNameError(null)
    }

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') handleCreate()
        if (e.key === 'Escape') {
            setShowForm(false)
            setName('')
            setNameError(null)
        }
    }

    const handleCancel = () => {
        setShowForm(false)
        setName('')
        setNameError(null)
    }

    return (
        <>
            <WorkspaceSettingsTitle>API</WorkspaceSettingsTitle>
            <FormDivider />
            <FormColumn>
                <FormColumn gap={16}>
                    <div className={css.title_row}>
                        <FormTitle>API keys</FormTitle>
                        <a href="/docs/api" target="_blank" rel="noopener noreferrer" className={css.docs_link}>API reference →</a>
                    </div>
                    <TextNorm>Use API keys to access your workspace data from external tools and integrations.</TextNorm>

                    {newKey && <NewKeyBanner apiKey={newKey} />}

                    {!newKey && (
                        <>
                            {isLoading && (
                                <div className={css.loader_frame}>
                                    <PlateLoader />
                                </div>
                            )}
                            {!isLoading && keys.length > 0 && (
                                <div className={css.frame}>
                                    {keys.map((k: ApiKey) => (
                                        <ApiKeyRow key={k.id} apiKey={k} />
                                    ))}
                                </div>
                            )}

                            {showForm ? (
                                <FormColumn>
                                    <FormLabel>Key name</FormLabel>
                                    <FormRow>
                                        <Input
                                            placeholder="e.g. Zapier integration"
                                            value={name}
                                            onChange={e => {
                                                setName(e.target.value)
                                                setNameError(null)
                                            }}
                                            onKeyDown={handleKeyDown}
                                            error={!!nameError}
                                        />
                                        <Button
                                            title="Create"
                                            type="black"
                                            size="medium"
                                            onClick={handleCreate}
                                            loading={isCreating}
                                        />
                                        <Button
                                            title="Cancel"
                                            type="white"
                                            size="medium"
                                            onClick={handleCancel}
                                        />
                                    </FormRow>
                                    <FormError>{nameError}</FormError>
                                </FormColumn>
                            ) : (
                                <div>
                                    <Button
                                        title="Create API key"
                                        type="white"
                                        size="medium"
                                        loading={isCreating}
                                        onClick={() => setShowForm(true)}
                                    />
                                </div>
                            )}
                        </>
                    )}
                </FormColumn>
            </FormColumn>
        </>
    )
}

export default WorkspaceSettingsModalApi
